import type { ChangeEvent } from 'react';
import { DataGrid } from '../../components/data-grid/DataGrid';
import type { GridColumn } from '../../components/data-grid/DataGrid';
import { formatDate } from '../../components/data-grid/format';
import { formatMoney } from '../../components/data-grid/money';
import type { Problem } from '../../components/data-grid/problem';
import { useI18n } from '../../i18n/I18nProvider';
import { filterDocuments, paginate, statusKey } from './filters';
import type { DocumentFilters, DocumentPeriod, DocumentRow, DocumentStatus, StatusFilter } from './filters';

export interface DocumentTypeOption {
  code: string;
  name: string;
}

export interface DocumentsViewProps {
  types: readonly DocumentTypeOption[];
  periods: readonly DocumentPeriod[];
  orgs?: ReadonlyArray<{ id: string; name: string }>;
  rows: readonly DocumentRow[];
  filters: DocumentFilters;
  page: number;
  pageSize: number;
  problem?: Problem | null;
  loading?: boolean;
  onFiltersChange(next: DocumentFilters): void;
  onApply(): void;
  onReset(): void;
  onPageChange(page: number): void;
  onOpen(row: DocumentRow): void;
  onNew(): void;
}

const STATUSES: DocumentStatus[] = ['draft', 'pending', 'approved', 'posted', 'cancelled'];

const STATUS_COLOR: Record<DocumentStatus, string> = {
  draft: 'var(--dim)',
  pending: '#b8860b',
  approved: '#2f6690',
  posted: '#1f7a4d',
  cancelled: '#a33',
};

export function StatusBadge({ status }: { status: DocumentStatus }): JSX.Element {
  const { t } = useI18n();
  return (
    <span
      className="badge"
      data-status={status}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <span
        aria-hidden="true"
        style={{ width: 6, height: 6, borderRadius: 3, background: STATUS_COLOR[status] }}
      />
      {t(statusKey(status))}
    </span>
  );
}

export function DocumentsView({
  types,
  periods,
  orgs = [],
  rows,
  filters,
  page,
  pageSize,
  problem,
  loading = false,
  onFiltersChange,
  onApply,
  onReset,
  onPageChange,
  onOpen,
  onNew,
}: DocumentsViewProps): JSX.Element {
  const { t, language } = useI18n();
  const filtered = filterDocuments(rows, filters, periods);
  const paged = paginate(filtered, page, pageSize);

  const onType = (event: ChangeEvent<HTMLSelectElement>): void => {
    onFiltersChange({ ...filters, type: event.target.value });
  };
  const onPeriod = (event: ChangeEvent<HTMLSelectElement>): void => {
    onFiltersChange({ ...filters, periodId: event.target.value });
  };
  const onStatus = (event: ChangeEvent<HTMLSelectElement>): void => {
    onFiltersChange({ ...filters, status: event.target.value as StatusFilter });
  };
  const onSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    onFiltersChange({ ...filters, search: event.target.value });
  };

  const columns: Array<GridColumn<DocumentRow>> = [
    {
      key: 'number',
      label: t('documentsColumnNumber'),
      render: (row) => (
        <a
          href={`/documents/${row.id}`}
          onClick={(event) => {
            event.preventDefault();
            onOpen(row);
          }}
        >
          {row.number}
        </a>
      ),
    },
    { key: 'type', label: t('documentsColumnType'), render: (row) => row.typeName },
    { key: 'date', label: t('documentsColumnDate'), render: (row) => formatDate(row.date) },
    { key: 'description', label: t('documentsColumnDescription'), render: (row) => row.description },
    {
      key: 'amount',
      label: t('documentsColumnAmount'),
      align: 'right',
      render: (row) => formatMoney(row.amount, language),
    },
    { key: 'status', label: t('documentsColumnStatus'), render: (row) => <StatusBadge status={row.status} /> },
    { key: 'author', label: t('documentsColumnAuthor'), render: (row) => row.author },
    {
      key: 'open',
      label: '',
      align: 'right',
      render: (row) => (
        <a
          href={`/documents/${row.id}`}
          onClick={(event) => {
            event.preventDefault();
            onOpen(row);
          }}
          aria-label={t('documentsColumnNumber')}
        >
          →
        </a>
      ),
    },
  ];

  const selectStyle = { minWidth: 160 };

  return (
    <section>
      <div className="page-head" style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1>{t('documentsTitle')}</h1>
          <p className="muted">{t('documentsSubtitle')}</p>
        </div>
        <button type="button" className="btn btn--primary" data-testid="documents-new" onClick={onNew}>
          {t('documentsNew')}
        </button>
      </div>

      <div className="card" style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <label className="field" style={selectStyle}>
          <span>{t('documentsFilterType')}</span>
          <select className="input" name="type" value={filters.type} onChange={onType}>
            <option value="">{t('documentsAll')}</option>
            {types.map((type) => (
              <option key={type.code} value={type.code}>
                {type.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field" style={selectStyle}>
          <span>{t('documentsFilterPeriod')}</span>
          <select className="input" name="period" value={filters.periodId} onChange={onPeriod}>
            <option value="">{t('documentsAllPeriods')}</option>
            {periods.map((period) => (
              <option key={period.id} value={period.id}>
                {t(`documentsMonth${period.month}`)} {period.year}
              </option>
            ))}
          </select>
        </label>

        {orgs.length > 1 ? (
          <label className="field" style={selectStyle}>
            <span>{t('documentsFilterOrg')}</span>
            <select
              className="input"
              name="org"
              value={filters.orgId}
              onChange={(event) => onFiltersChange({ ...filters, orgId: event.target.value })}
            >
              <option value="">{t('documentsAllOrgs')}</option>
              {orgs.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label className="field" style={selectStyle}>
          <span>{t('documentsFilterStatus')}</span>
          <select className="input" name="status" value={filters.status} onChange={onStatus}>
            <option value="all">{t('documentsAll')}</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(statusKey(status))}
              </option>
            ))}
          </select>
        </label>

        <label className="field" style={{ flex: 1, minWidth: 200 }}>
          <span>{t('documentsFilterSearch')}</span>
          <input
            className="input"
            name="search"
            value={filters.search}
            placeholder={t('documentsFilterSearchPlaceholder')}
            onChange={onSearch}
          />
        </label>

        <button type="button" className="btn btn--primary" onClick={onApply}>
          {t('documentsFilterApply')}
        </button>
        <button type="button" className="btn" onClick={onReset}>
          {t('documentsFilterReset')}
        </button>
      </div>

      {problem !== null && problem !== undefined ? (
        <div className="alert alert--danger" role="alert" data-testid="documents-problem" style={{ marginTop: 12 }}>
          <strong>{t('documentsProblemTitle')}</strong> <code>{problem.code}</code> — {problem.detail}
        </div>
      ) : null}

      <div style={{ marginTop: 16 }}>
        {loading ? (
          <p className="muted" data-testid="documents-loading">
            {t('documentsLoading')}
          </p>
        ) : (
          <DataGrid
            testId="documents-grid"
            columns={columns}
            rows={paged.items}
            getRowKey={(row) => row.id}
            rowHeight={40}
            viewportHeight={440}
            emptyText={t('documentsEmpty')}
            onRowClick={onOpen}
          />
        )}
      </div>

      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}
        data-testid="documents-pager"
      >
        <span className="muted">
          {paged.from}–{paged.to} / {paged.total} {t('documentsRows')}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="btn"
            disabled={paged.page <= 1}
            onClick={() => onPageChange(paged.page - 1)}
          >
            {t('documentsPrev')}
          </button>
          <span className="muted">
            {paged.page} / {paged.pageCount} {t('documentsPage')}
          </span>
          <button
            type="button"
            className="btn"
            disabled={paged.page >= paged.pageCount}
            onClick={() => onPageChange(paged.page + 1)}
          >
            {t('documentsNext')}
          </button>
        </span>
      </div>
    </section>
  );
}
